import { useState, useEffect } from "react";
import classnames from "classnames";
import { getStorage, ref, getDownloadURL, getMetadata, deleteObject } from "firebase/storage";
import app from "fb";
import { toast } from "react-toastify";

interface PropsType {
    value: string;
    size?: "medium" | "large";
    onDelete: () => void;
    disabled?: boolean
}

export function InputImagePreview({
    value,
    size = "medium",
    onDelete,
    disabled = false,
}: PropsType) {
    const [url, setUrl] = useState<string | undefined>(undefined);
    const [err, setErr] = useState<string | undefined>(undefined);

    useEffect(() => {

        // Download the image using the URL in the input field
        if (value !== '') {
            setUrl(undefined);

            const storage = getStorage(app);
            const storageRef = ref(storage, value);
            getMetadata(storageRef).then((metadata) => {
                if (metadata?.customMetadata?.previewImageURL) {
                    setUrl(metadata?.customMetadata?.previewImageURL);
                } else if (metadata?.customMetadata?.downloadURL) {
                    setUrl(metadata?.customMetadata?.downloadURL);
                } else {
                    getDownloadURL(storageRef)
                        .then((url) => {
                            setUrl(url);
                        })
                        .catch((error) => {
                            setUrl("");
                            setErr("Not exist in media store");
                            console.log('Error getting image from storage', error);
                        });
                }
            }).catch((error) => {
                setUrl("");
                setErr("Not exist in media store");
                console.log(error);
            });
        }
    }, [value, size]);


    const _onDelete = () => {
        if (!!value && url === "") {
            onDelete();
        } else if (!!value && !!url) {
            const storage = getStorage(app);
            const storageRef = ref(storage, value);
            deleteObject(storageRef).then(() => {
                setUrl("");
                onDelete();
            }).catch((error: any) => {
                toast.error(error.message ?? "Delete fail")
            });
        }
    }

    if (size === "large") {
        return (
            <ItemLarge
                url={url}
                err={err}
                disabled={disabled}
                onDelete={_onDelete}
            />
        );
    }

    return (
        <ItemBasic
            url={url}
            err={err}
            disabled={disabled}
            onDelete={_onDelete}
        />
    );
}

interface PropsTypeUrl {
    url: string;
    size?: "medium" | "large";
    onDelete: () => void;
    disabled?: boolean
}

export function InputImagePreviewUrl({
    url,
    size = "medium",
    onDelete,
    disabled = false,
}: PropsTypeUrl) {

    if (size === "large") {
        return (
            <ItemLarge
                url={url}
                disabled={disabled}
                onDelete={onDelete}
            />
        );
    }

    return (
        <ItemBasic
            url={url}
            disabled={disabled}
            onDelete={onDelete}
        />
    );
}


function ItemLarge({url, err, disabled = false, onDelete} : {url: string  | undefined, err?: string | undefined, disabled?: boolean, onDelete: () => void})  {
    return (
        <div className="relative flex items-center justify-center w-full">
            <label
                htmlFor="dropzone-file"
                className={classnames(
                    "fle flex-col items-center justify-center w-full border-2 border-gray-300 border-dashed rounded-lg overflow-hidden dark:bg-gray-700 dark:border-gray-60",
                    {
                        "bg-gray-50": !disabled,
                        "bg-gray-200 cursor-not-allowed": disabled,
                    }
                )}
            >
                <div className={"flex flex-col items-center justify-center h-64"}>
                    {
                        url === undefined ? <svg aria-hidden="true" role="status" className="inline w-8 h-8 text-black animate-spin fill-slate-800" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="#E5E7EB" />
                            <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentColor" />
                        </svg> : url === "" ? (
                            <>
                                <svg className="w-8 h-8 mb-4 text-gray-500 dark:text-gray-400" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m3 16 5-7 6 6.5m6.5 2.5L16 13l-4.3 6m2.3-9h0M4 19h16c.6 0 1-.4 1-1V6c0-.6-.4-1-1-1H4a1 1 0 0 0-1 1v12c0 .6.4 1 1 1Z" />
                                </svg>
                                <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
                                    {err ?? "Error"}
                                </p>
                            </>
                        ) :
                            <img src={url} alt="Preview" className="object-contain h-full w-full" />
                    }
                </div>
            </label>
            {!disabled && url !== undefined && (
                <button
                    type="button"
                    onClick={onDelete}
                    className="absolute top-3 end-3 text-gray-800 bg-gray-200 hover:bg-gray-300 focus:ring-0 focus:outline-none font-medium rounded-full text-sm p-1.5 text-center inline-flex items-center dark:bg-gray-800 dark:hover:bg-gray-700"
                >
                    <svg className="w-3 h-3" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18 18 6m0 12L6 6" />
                    </svg>
                    <span className="sr-only">Icon close</span>
                </button>
            )}
        </div>
    );
}

function ItemBasic({url, err, disabled = false, onDelete} : {url: string  | undefined, err?: string | undefined, disabled?: boolean, onDelete: () => void})  {
    return (
        <div className="relative flex items-center justify-center w-full max-w-56">
            <label
                className={
                    classnames(
                        "flex flex-col items-center justify-center w-full aspect-square rounded-lg overflow-hidden dark:bg-gray-700 dark:border-gray-600",
                        {
                            "bg-gray-50": !disabled,
                            "bg-gray-200 cursor-not-allowed": disabled,
                            "cursor-pointer dark:hover:border-gray-500 dark:hover:bg-gray-600 dark:hover:bg-bray-800 hover:bg-gray-200": !url,
                        }
                    )
                }
            >
                {
                    url === undefined ? (
                        <svg aria-hidden="true" role="status" className="inline w-5 h-5 text-black animate-spin fill-slate-800" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="#E5E7EB" />
                            <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentColor" />
                        </svg>
                    ) :
                        url === "" ? (
                            <span className="text-center text-sm m-4 text-gray-500">
                                {err}
                            </span>
                        ) : <img src={url} alt="Preview" className="object-contain h-full w-full" />
                }
            </label>
            {!disabled && url !== undefined && (
                <button
                    type="button"
                    onClick={onDelete}
                    className="absolute top-1 end-1 text-gray-800 bg-gray-200 hover:bg-gray-300 focus:ring-0 focus:outline-none font-medium rounded-full text-sm p-1 text-center inline-flex items-center dark:bg-gray-800 dark:hover:bg-gray-700"
                >
                    <svg className="w-3 h-3" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18 18 6m0 12L6 6" />
                    </svg>
                    <span className="sr-only">Icon close</span>
                </button>
            )}

        </div>
    );
}